import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FC, type ReactNode } from "react";
import { SkuDelProducto } from "./SkuDelProducto";
import { createPortal } from "react-dom";
import { ChevronLeft, Film, Loader2, X, type LucideIcon, Check } from "lucide-react";
import { clasesDePastilla } from "./pastillaDeCarpeta";
import { AbanicoDeFotos } from "./AbanicoDeFotos";
import { useDebajoDeMd } from "../hooks/useEsMobile";

// Selector de imagen para los slots de una escena IA (frame inicial, frame
// final, referencias). Es un pop-up CENTRADO con pestañas: cada pestaña es una
// fuente de imágenes de la cuenta y muestra sus miniaturas, en vez del viejo
// dropdown que sólo nombraba las fuentes y te mandaba a otro panel a buscarlas.
// Ver ADR-2026-09-16-el-slot-de-imagen-abre-un-selector-centrado.
//
// El componente es de presentación: no sabe de assets, proyectos ni imports.
// Quien lo monta le pasa los items ya resueltos y qué hacer cuando se elige uno.

/** Acción que no es una grilla: subir un archivo, generar, sacar de un video. */
/** El tile de un video: su primer cuadro (`preload="metadata"` no baja el archivo
 *  entero) y un rótulo, para no confundirlo con una foto. */
const TileDeVideo: FC<{ url: string }> = ({ url }) => (
  <>
    <video
      src={url}
      muted
      playsInline
      preload="metadata"
      className="h-full w-full object-cover pointer-events-none"
    />
    <span className="absolute bottom-1 left-1 inline-flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white pointer-events-none">
      <Film className="w-2.5 h-2.5" />
      Video
    </span>
  </>
);

/** El gris del esqueleto: un punto más oscuro que el fondo de los tiles (`#F0F2F7`),
 *  para que el pulso se vea también encima de ellos. */
const CLASE_ESQUELETO = "animate-pulse bg-[#E9ECF2]";

/** Un tile vacío que late mientras la pestaña trae su lista (`conEsqueleto`). Con
 *  nombres lleva también el renglón del nombre, así la grilla no salta al llegar. */
const TileEsqueleto: FC<{ claseDeTile: string; conNombre: boolean }> = ({
  claseDeTile,
  conNombre,
}) =>
  conNombre ? (
    <div aria-hidden="true" className="flex min-w-0 flex-col gap-1.5">
      <span className={`block ${claseDeTile} w-full rounded-xl ${CLASE_ESQUELETO}`} />
      <span className={`my-0.5 block h-3 w-2/3 rounded ${CLASE_ESQUELETO}`} />
    </div>
  ) : (
    <div aria-hidden="true" className={`${claseDeTile} rounded-[10px] ${CLASE_ESQUELETO}`} />
  );

/**
 * El retardo de entrada de la ficha `i` al cambiar de pestaña: de a 22 ms, con techo en la
 * ficha 12 para que la cascada no se haga larga (y para que la página que se suma al
 * scrollear entre sin esperar). Va con la clase `animate-ficha-de-pestana`, que sólo corre
 * al montar: las fichas que siguen de una pestaña a la otra no vuelven a animar.
 */
const entradaEscalonada = (i: number): CSSProperties => ({
  animationDelay: `${Math.min(i, 12) * 22}ms`,
});

/** La miniatura de un item. Con `esqueleto`, el tile late hasta que la imagen terminó de
 *  bajar y ahí aparece con un fundido: una subida puede tardar (Storage arma la
 *  miniatura la primera vez que se pide) y sin esto el tile quedaba en blanco. Quien la
 *  monta la keyea por URL, así una imagen nueva vuelve a empezar. */
const ImagenDelTile: FC<{ url: string; alt: string; className: string; esqueleto: boolean }> = ({
  url,
  alt,
  className,
  esqueleto,
}) => {
  const ref = useRef<HTMLImageElement | null>(null);
  const [cargada, setCargada] = useState(false);
  // Si ya estaba en el cache del navegador puede estar completa antes del `onLoad`.
  useEffect(() => {
    if (ref.current?.complete && ref.current.naturalWidth > 0) setCargada(true);
  }, []);
  if (!esqueleto) {
    return (
      <img src={url} alt={alt} loading="lazy" decoding="async" className={className} />
    );
  }
  return (
    <>
      {!cargada && <span aria-hidden="true" className={`absolute inset-0 ${CLASE_ESQUELETO}`} />}
      <img
        ref={ref}
        src={url}
        alt={alt}
        loading="lazy"
        decoding="async"
        // Un error también corta el pulso: un tile latiendo para siempre dice "ya viene".
        onLoad={() => setCargada(true)}
        onError={() => setCargada(true)}
        className={`${className} transition-opacity duration-200 ${cargada ? "opacity-100" : "opacity-0"}`}
      />
    </>
  );
};

/** Cuántos tiles de esqueleto se dibujan mientras carga la lista (acciones incluidas):
 *  tres filas de cinco, lo que llena el cuerpo en escritorio. */
const TILES_DE_ESQUELETO = 15;

export type AccionDelSelector = {
  id: string;
  label: string;
  Icon: LucideIcon;
  /** `ia` pinta el ícono con el color de marca (es lo que hace la magia). */
  tono?: "ia" | "neutro";
  /** El renglón chico debajo del label ("Fotos y nombre"). Sólo lo dibuja el estilo
   *  `tarjeta` (ver `estiloDeAccion`). */
  sublabel?: string;
  /** La acción está corriendo (subiendo, procesando): la tarjeta muestra el spinner y
   *  no se puede volver a tocar. Sólo el estilo `tarjeta`. */
  ocupado?: boolean;
  /** El dibujo de la foto del frente del abanico (un retrato, una pose). Sin esto, el
   *  ícono de imagen. Sólo el estilo `tarjeta`. */
  dibujo?: React.ReactNode;
  onClick: () => void;
};

export type ItemDelSelector = {
  id: string;
  /** Puede faltar mientras se resuelve la signed URL: el tile queda en gris. */
  url?: string;
  nombre: string;
  /** Productos: el SKU va en gris debajo del nombre (ADR-2026-10-22-el-sku-se-ve-debajo-del-nombre). */
  sku?: string | null;
  /** Subiendo/importando: tile bloqueado con spinner. */
  importando?: boolean;
  /** Un video del proyecto (sólo el slot de referencia los ofrece): el tile es un
   *  `<video>` con su primer cuadro y un rótulo, no un `<img>` roto. */
  esVideo?: boolean;
  /**
   * Elegido en una selección MÚLTIPLE: el tile lleva el aro azul y un tilde. Lo usa "Elegí
   * qué agregar", donde tocar marca en vez de agregar y se confirma en el `pie`
   * (ADR-2026-10-04-el-selector-de-fuentes-elige-varios). Sin esto, el tile de siempre.
   */
  seleccionado?: boolean;
};

export type PestanaDelSelector = {
  id: string;
  label: string;
  Icon: LucideIcon;
  items: ItemDelSelector[];
  cargando: boolean;
  /** Qué decir cuando la pestaña no tiene nada. Vacío (`''`) no dice nada: la pestaña
   *  dibuja lo suyo en `encabezado` (las carpetas de Moodboards, con su "Nueva carpeta"). */
  vacio: string;
  /** Los personajes son fotos de cuerpo entero: recortarlas a cuadrado las corta por la mitad. */
  aspecto?: "cuadrado" | "retrato";
  /** `entera`: la miniatura se ve completa (`object-contain`) en vez de recortada. Para
   *  logos y elementos gráficos, que recortados dejan de ser lo que son
   *  (ADR-2026-10-19-elementos-de-marca). Sin esto, recortada como siempre. */
  encuadre?: "cubrir" | "entera";
  /** El nombre de cada item va debajo de su miniatura, como en la biblioteca del Agente
   *  (ADR-2026-09-25-el-selector-de-fuentes-toma-la-biblioteca). Sin esto el nombre es
   *  sólo el `title`. */
  conNombres?: boolean;
  /** Las acciones (las de `acciones`, las del pop-up entero) van al frente de ESTA grilla. */
  conAcciones?: boolean;
  /**
   * Acciones PROPIAS de esta pestaña, al frente de su grilla. Las de `acciones` valen
   * para el pop-up entero (una sola pestaña las muestra, la del `conAcciones`); estas
   * valen para una fuente sola, porque dan de alta EN ella: "Nuevo producto" en
   * Productos (ADR-2026-09-22-el-alta-de-producto-adentro-del-selector-de-fuentes).
   */
  acciones?: AccionDelSelector[];
  /**
   * Lo que va ARRIBA de la grilla de esta pestaña, a todo el ancho y dentro del scroll:
   * las dos tarjetas de subida de Productos en el modo Crear
   * (ADR-2026-10-16-subir-en-dos-tarjetas-en-todos-los-modos).
   */
  encabezado?: ReactNode;
  onElegir: (item: ItemDelSelector) => void;
};

type Props = {
  /** Qué se está eligiendo. Es el título del pop-up: "Elegí el frame inicial". */
  titulo: string;
  pestanas: PestanaDelSelector[];
  pestanaActiva: string;
  onCambiarPestana: (id: string) => void;
  acciones: AccionDelSelector[];
  /**
   * Un renglón de aviso debajo de las pestañas: para qué no salió, sin cerrar el
   * pop-up. Lo usa el selector de fuentes de la caja del Agente (el tope de archivos
   * por mensaje, un archivo que no se pudo leer); el Estudio no manda ninguno y ahí no
   * se dibuja nada (ADR-2026-09-21-la-caja-de-la-home-abre-el-selector-de-fuentes).
   */
  aviso?: string | null;
  /**
   * Una SUB-VISTA que se come el cuerpo del pop-up: en vez de la grilla, lo que mande
   * quien lo monta, con las pestañas reemplazadas por un "volver". Es para los flujos que
   * salen de una acción de la grilla y terminan volviendo a ella —el alta de producto del
   * selector de fuentes— sin abrir una segunda ventana encima
   * (ADR-2026-09-22-el-alta-de-producto-adentro-del-selector-de-fuentes).
   *
   * La cabecera dice A DÓNDE se vuelve (`volverA`: "Productos"), no qué se está haciendo:
   * el contenido ya trae su propio título, y repetirlo dejaba el mismo rótulo dos veces,
   * uno encima del otro. El contenido recibe el alto entero (`h-full`) y trae su propio
   * padding: el cuerpo no le pone el de la grilla.
   */
  vista?: {
    /** La fuente a la que vuelve la flecha. */
    volverA: string;
    onVolver: () => void;
    contenido: React.ReactNode;
  } | null;
  /**
   * Lo que va entre las pestañas y la grilla: el buscador y el orden de la pestaña
   * abierta, armados por quien monta el pop-up (el selector de fuentes del Agente). El
   * pop-up no sabe qué se busca ni cómo se ordena: recibe los items ya filtrados.
   */
  herramientas?: React.ReactNode;
  /**
   * Cómo se dibujan las acciones al frente de la grilla. `punteado` (el default) es el
   * cuadrado de borde punteado de los slots del Estudio; `tarjeta` es la tarjeta de subir
   * de los modos y de Mi marca —punteado celeste, degradé y el abanico de fotos, con
   * título y bajada—, así "Elegí qué agregar" da de alta con la misma cara que "Subir
   * nuevo producto" en los pickers
   * (ADR-2026-10-09-las-altas-de-elegi-que-agregar-con-la-cara-de-subir-producto).
   */
  estiloDeAccion?: "punteado" | "tarjeta";
  /**
   * Las columnas de la grilla, como clases de Tailwind (tienen que ser literales: el
   * JIT escanea el texto). El default es la grilla de los slots del Estudio
   * (3 / 4 / 6); el selector de fuentes va a 2 en el teléfono y 5 en escritorio, que
   * con el nombre debajo es lo que entra sin achicar la miniatura.
   */
  claseDeGrilla?: string;
  /**
   * Cuánto mide la hoja en el TELÉFONO. `hoja` (el default) es la geometría de la hoja de
   * herramientas del Estudio, `min(72vh, 620px)`; `completa` llega hasta arriba, como
   * las hojas de productos del chat, y es lo que pide "Elegí qué agregar": con el
   * buscador y las tarjetas de alta, a 72vh entraba una fila y media de miniaturas
   * (ADR-2026-09-25-agregar-con-rotulo-y-la-hoja-entera-en-el-telefono). En escritorio
   * no cambia nada.
   */
  altoEnElTelefono?: "hoja" | "completa";
  /**
   * `false` mientras SALE: el pop-up se dibuja con su estilo de cerrado (el fondo se apaga,
   * la hoja baja en el teléfono y en escritorio se achica y se desvanece) y quien lo monta
   * lo desmonta cuando la transición terminó (`useAperturaAnimada`, `DURACION_SALIDA_MS`).
   * Sin esto el pop-up desaparece de golpe
   * (ADR-2026-09-25-el-selector-de-fuentes-entra-y-sale-con-transicion). La ENTRADA no
   * la pide nadie: el pop-up se monta cerrado y se abre solo en el frame siguiente.
   */
  visible?: boolean;
  /**
   * Hay más items que los dibujados: el pop-up pone un centinela al pie de la grilla y
   * llama `onLlegarAlFinal` cuando queda a la vista (y al montar, si la grilla no llena
   * el cuerpo). Quien lo monta decide qué es "más": dibujar otra página de lo que ya
   * tiene, o pedirle otra a la base
   * (ADR-2026-09-25-el-selector-de-fuentes-carga-miniaturas-y-por-scroll).
   */
  quedaPorMostrar?: boolean;
  onLlegarAlFinal?: () => void;
  /**
   * Cargando, en vez del spinner en el centro, la grilla ya armada: las acciones de la
   * pestaña (el "Subir archivo") a la vista y tiles de esqueleto en lugar de las
   * miniaturas; y cada miniatura late hasta que su imagen bajó. Lo pide "Elegí qué
   * agregar", donde firmar las subidas tarda y el spinner tapaba justo la tarjeta para
   * subir (ADR-2026-09-27-el-selector-de-fuentes-carga-con-esqueleto). El Estudio no lo
   * manda y queda como estaba.
   */
  conEsqueleto?: boolean;
  /**
   * Una barra fija al PIE del pop-up, debajo de la grilla (no se va con el scroll ni se
   * dibuja en una sub-vista). "Elegí qué agregar" pone ahí el botón que confirma la
   * selección múltiple (ADR-2026-10-04-el-selector-de-fuentes-elige-varios).
   */
  pie?: React.ReactNode;
  onCerrar: () => void;
};

/** Cuánto dura la transición de salida. Quien pasa `visible` espera esto para desmontar. */
export const DURACION_SALIDA_MS = 200;

const SelectorDeImagenModal = ({
  titulo,
  pestanas,
  pestanaActiva,
  onCambiarPestana,
  acciones,
  aviso,
  vista,
  herramientas,
  estiloDeAccion = "punteado",
  claseDeGrilla = "grid-cols-3 sm:grid-cols-4 md:grid-cols-6",
  altoEnElTelefono = "hoja",
  visible = true,
  quedaPorMostrar = false,
  onLlegarAlFinal,
  conEsqueleto = false,
  pie,
  onCerrar,
}: Props) => {
  // El centinela del final, medido por GEOMETRÍA y no con un `IntersectionObserver`: la
  // grilla vive en un cuerpo que scrollea por su cuenta y el observer depende de acertar
  // el `root` (ADR-2026-09-04-el-centinela-se-mide-por-geometria, la columna de
  // resultados). El listener de scroll va en CAPTURA para que el scroll del cuerpo —que
  // no burbujea— llegue igual. Se chequea también al montar y al cambiar de pestaña.
  const centinelaRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!quedaPorMostrar || !onLlegarAlFinal) return;
    const chequear = () => {
      const el = centinelaRef.current;
      if (!el) return;
      // 300px antes de llegar: la página siguiente entra mientras todavía se scrollea.
      if (el.getBoundingClientRect().top <= window.innerHeight + 300) onLlegarAlFinal();
    };
    chequear();
    window.addEventListener("scroll", chequear, true);
    window.addEventListener("resize", chequear);
    return () => {
      window.removeEventListener("scroll", chequear, true);
      window.removeEventListener("resize", chequear);
    };
  }, [quedaPorMostrar, onLlegarAlFinal]);
  // La entrada: se monta con el estilo de cerrado y en el frame siguiente pasa al de
  // abierto, así hay transición que correr. Dos frames y no uno: con uno el navegador
  // puede pintar los dos estados juntos y el pop-up aparece de golpe (es lo que hacen las
  // hojas de la app). Antes iba con `animate-in fade-in zoom-in-95 …`, clases de un
  // plugin que este proyecto NO tiene: no compilaban y el pop-up aparecía de golpe.
  const [entro, setEntro] = useState(false);
  useEffect(() => {
    let segundo = 0;
    const primero = window.requestAnimationFrame(() => {
      segundo = window.requestAnimationFrame(() => setEntro(true));
    });
    return () => {
      window.cancelAnimationFrame(primero);
      window.cancelAnimationFrame(segundo);
    };
  }, []);
  const mostrado = visible && entro;
  // Escape cierra. El pop-up tapa el panel entero, así que sin esto la única
  // salida es apuntarle a la X. Con una sub-vista abierta, Escape VUELVE a la
  // grilla en vez de cerrar: adentro puede haber trabajo a medio cargar (las
  // fotos de un producto nuevo) y un cierre entero lo perdería.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (vista) vista.onVolver();
      else onCerrar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCerrar, vista]);

  // Sólo para el alto de la hoja del teléfono: en `md` el alto lo pone la clase, y un
  // `style` inline le ganaría. El corte es el mismo `md` de la geometría de la hoja.
  const esEscritorio = !useDebajoDeMd();
  const activa = pestanas.find((p) => p.id === pestanaActiva) ?? pestanas[0];
  const retrato = activa?.aspecto === "retrato";
  const ajusteDeImagen = activa?.encuadre === "entera" ? "object-contain p-2" : "object-cover";
  const claseDeTile = retrato ? "aspect-[3/4]" : "aspect-square";
  // Las del pop-up entero (sólo en la pestaña marcada) + las propias de la pestaña.
  const accionesDeLaGrilla = [
    ...(activa?.conAcciones ? acciones : []),
    ...(activa?.acciones ?? []),
  ];

  // ── Cambiar de pestaña con transición
  //    (ADR-2026-10-09-las-pestanas-de-elegi-que-agregar-cambian-con-transicion).
  //    El fondo celeste de la pastilla activa es UNO solo que se desliza hasta la pastilla
  //    tocada (se mide la pastilla: las pestañas se parten en dos renglones en el teléfono),
  //    y las fichas de la pestaña nueva entran escalonadas (`entradaEscalonada`).
  const tiraRef = useRef<HTMLDivElement | null>(null);
  const [indicador, setIndicador] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  /** La primera medición se pinta sin transición: si no, el fondo entraría volando desde
   *  la esquina al abrir el pop-up. */
  const [indicadorListo, setIndicadorListo] = useState(false);
  useLayoutEffect(() => {
    const tira = tiraRef.current;
    if (!tira) return;
    const medir = () => {
      const el = tira.querySelector<HTMLElement>("[data-pestana-activa]");
      setIndicador(
        el ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight } : null,
      );
    };
    medir();
    // Las pastillas cambian de lugar si cambia el ancho (o una etiqueta, como "Modelos" /
    // "Avatares" al cambiar de marca).
    const observador = new ResizeObserver(medir);
    observador.observe(tira);
    return () => observador.disconnect();
  }, [activa?.id, vista]);
  useEffect(() => {
    if (!indicador || indicadorListo) return;
    const raf = window.requestAnimationFrame(() => setIndicadorListo(true));
    return () => window.cancelAnimationFrame(raf);
  }, [indicador, indicadorListo]);
  // La pestaña nueva arranca arriba: con el scroll de la anterior, el escalonado de la
  // primera fila pasaba fuera de la vista.
  const cuerpoRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    cuerpoRef.current?.scrollTo({ top: 0 });
  }, [activa?.id]);

  /* VA POR PORTAL A `body` — ADR-2026-09-22-el-selector-de-imagen-va-por-portal.
     `position: fixed` + `z-[120]` no alcanza: el z-index se mide DENTRO del contexto de
     apilamiento más cercano, y montado desde la caja del Agente de la home el pop-up
     quedaba adentro de un `isolate` (`isolation: isolate` crea uno). Ahí su 120 no competía
     con el resto de la página: los íconos de arriba a la derecha, la tira de videos y la
     barra de abajo se dibujaban ENCIMA de la hoja, y el `overflow-x-clip` del mismo
     ancestro podía recortarla. Colgado de `body` no hay ancestro que lo acote. */
  return createPortal(
    <div
      /* En el teléfono se apoya ABAJO y ocupa el ancho entero; en escritorio va
         centrado con aire alrededor. El corte es `md`, el mismo de
         `useDebajoDeMd()` con el que el editor decide todo lo demás. */
      className={`fixed inset-0 z-[120] flex items-end justify-center p-0 md:items-center md:p-4 bg-black/40 backdrop-blur-[2px] transition-opacity duration-200 ease-out motion-reduce:transition-none ${
        mostrado ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      onClick={() => (vista ? vista.onVolver() : onCerrar())}
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
    >
      <div
        /* Apaisada y de alto FIJO (no `max-h`): cambiar de pestaña no tiene
           que cambiar el tamaño de la ventana. Una pestaña con pocas imágenes
           —o vacía— deja el espacio en blanco abajo en vez de encogerla y
           mover las pastillas de arriba bajo el cursor.

           El alto es un 20% más que el original (era `min(70vh,560px)`): con
           tres filas de miniaturas la cuarta quedaba siempre cortada al ras
           del borde. Las dos caras del `min` suben juntas — si sólo creciera
           el tope en px, en pantallas bajas el pop-up se saldría de la
           ventana. Ver ADR-2026-09-17-el-selector-de-imagen-es-mas-alto.

           En el TELÉFONO no es un recuadro flotando encima de la hoja de
           herramientas: es una hoja más, con la MISMA geometría que aquella
           —ancho completo, `min(72vh, 620px)` de alto, esquinas de arriba
           redondeadas— y entrando desde abajo. Un diálogo centrado sobre una
           hoja que sube desde el borde inferior se leía como dos capas
           peleadas, con el recuadro asomando por encima del borde de la hoja.
           El alto de escritorio queda como está: el `min(84vh,672px)` resuelve
           la cuarta fila cortada, que es un problema de la grilla ancha.
           Ver ADR-2026-09-17-el-selector-de-imagen-es-una-hoja-en-el-telefono.

           `overflow-hidden`: la ventana RECORTA lo que tiene adentro a sus esquinas
           redondeadas. Sin esto, una sub-vista que termina en una barra con fondo (el
           pie del alta de producto, blanco y de esquinas cuadradas) asomaba por las
           esquinas de abajo y se leía como una segunda ventana encima de ésta
           (ADR-2026-09-25-la-sub-vista-del-selector-no-asoma-por-las-esquinas). La
           grilla no lo mostraba porque el cuerpo no tiene fondo propio. */
        className={`bg-surface-card w-full overflow-hidden rounded-t-2xl shadow-xl flex flex-col md:max-w-[980px] md:h-[min(84vh,672px)] md:rounded-[14px] md:border md:border-neutral-200 transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none ${
          altoEnElTelefono === "completa" ? "" : "h-[min(72vh,620px)]"
        } ${
          /* Teléfono: la hoja sube desde abajo y baja al cerrar. Escritorio: aparece con
             un zoom mínimo (95 → 100) y un fundido, y sale al revés. Sutiles y cortas: el
             pop-up se abre y cierra muchas veces por sesión. */
          mostrado
            ? "translate-y-0 md:scale-100 md:opacity-100"
            : "translate-y-full md:translate-y-0 md:scale-95 md:opacity-0"
        }`}
        // El alto completo va en `style` y no en una clase arbitraria: es un `calc` con
        // `env()`, y en este repo esas clases se verifican contra el CSS compilado antes de
        // confiar en ellas. Es el mismo alto que las hojas de productos del chat: hasta el
        // techo, menos 1rem de aire y la zona segura de arriba. En `md` gana la clase.
        style={
          altoEnElTelefono === "completa" && !esEscritorio
            ? { height: "calc(100dvh - 1rem - env(safe-area-inset-top, 0px))" }
            : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera: qué se está eligiendo + las pestañas. Las pestañas van
            ACÁ arriba (y no flotando sobre la grilla) porque son la división
            más gruesa del pop-up: de dónde sale la imagen. */}
        <div
          className={`px-4 pt-3.5 border-b border-black/[0.06] shrink-0 ${vista ? "pb-3.5" : "pb-3"}`}
        >
          <div className={`flex items-start justify-between gap-3 ${vista ? "" : "mb-3"}`}>
            {/* Con una sub-vista abierta, la cabecera es la flecha de vuelta a la grilla:
                la X sigue cerrando el pop-up entero. */}
            {vista ? (
              <button
                type="button"
                onClick={vista.onVolver}
                aria-label={`Volver a ${vista.volverA}`}
                className="flex items-center gap-1.5 -ml-1 pl-1 pr-2 py-0.5 rounded-md text-brand-black/70 hover:bg-black/[0.04] hover:text-brand-black/90 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-brand-black/45" />
                <span className="text-[15px] font-semibold">{vista.volverA}</span>
              </button>
            ) : (
              <h3 className="text-[15px] font-semibold text-brand-black/90">
                {titulo}
              </h3>
            )}
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar"
              className="w-7 h-7 shrink-0 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:text-brand-black/70 hover:bg-black/[0.06] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Las pestañas no se dibujan en la sub-vista: cambiar de fuente ahí tiraría a
              la basura lo que se esté cargando, y el camino de vuelta es la flecha. */}
          <div ref={tiraRef} className={`relative items-center gap-1 flex-wrap ${vista ? "hidden" : "flex"}`}>
            {indicador && (
              <span
                aria-hidden="true"
                className={`pointer-events-none absolute left-0 top-0 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.1)] motion-reduce:transition-none ${
                  indicadorListo
                    ? "transition-[transform,width,height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
                    : ""
                }`}
                style={{
                  transform: `translate(${indicador.x}px, ${indicador.y}px)`,
                  width: indicador.w,
                  height: indicador.h,
                }}
              />
            )}
            {pestanas.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onCambiarPestana(p.id)}
                data-pestana-activa={p.id === activa?.id ? "" : undefined}
                // `relative` para ir por encima del fondo que se desliza; la activa pierde
                // su fondo propio (lo pone el que se desliza) una vez que está medido.
                className={`relative ${clasesDePastilla(p.id === activa?.id)} ${
                  p.id === activa?.id && indicador ? "!bg-transparent" : ""
                }`}
              >
                <p.Icon className="w-3.5 h-3.5" />
                {p.label}
              </button>
            ))}
          </div>
          {aviso && !vista && (
            <p role="status" className="mt-2 text-[12.5px] text-[#C33B36]">
              {aviso}
            </p>
          )}
        </div>

        {/* El buscador y el orden de la pestaña, si quien monta el pop-up los manda.
            Fuera del scroll, como las pestañas: se quedan a la vista mientras la grilla
            se recorre. */}
        {herramientas && !vista && (
          // `pb-3` además del `pt-3.5` del cuerpo: 26px entre el buscador y la primera
          // fila. Con los 14 del cuerpo solos, la grilla —que scrollea por debajo de esta
          // barra fija— quedaba pegada al buscador.
          <div className="shrink-0 px-4 pb-3 pt-3 animate-ficha-de-pestana">{herramientas}</div>
        )}

        {/* Cuerpo: la grilla, o la sub-vista cuando hay una (que trae su propio
            padding y su propio scroll). */}
        {vista ? (
          <div className="flex-1 min-h-0">{vista.contenido}</div>
        ) : (
        <div ref={cuerpoRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3.5">
          {activa?.cargando && activa.items.length === 0 && !conEsqueleto ? (
            <div className="flex items-center justify-center py-14">
              <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
            </div>
          ) : (
            <>
              {activa?.encabezado}
              <div className={`grid ${claseDeGrilla} ${estiloDeAccion === "tarjeta" ? "gap-x-3 gap-y-4" : "gap-2.5"}`}>
                {accionesDeLaGrilla.map((a, i) =>
                  estiloDeAccion === "tarjeta" ? (
                    <TarjetaDeAlta
                      key={a.id}
                      accion={a}
                      className={`${claseDeTile} ${activa?.conNombres ? "rounded-xl" : "rounded-[10px]"} animate-ficha-de-pestana`}
                      style={entradaEscalonada(i)}
                    />
                  ) : (
                    <button
                      key={a.id}
                      type="button"
                      onClick={a.onClick}
                      style={entradaEscalonada(i)}
                      className={`animate-ficha-de-pestana ${claseDeTile} rounded-[10px] border-2 border-dashed border-neutral-300 hover:border-brand-primary/50 hover:bg-brand-primary/[0.03] transition-colors flex flex-col items-center justify-center gap-1.5 px-2 text-center`}
                    >
                      <a.Icon
                        className={`w-5 h-5 ${a.tono === "ia" ? "text-brand-primary" : "text-neutral-400"}`}
                      />
                      <span className="text-[11px] font-medium text-brand-black/60 leading-tight">
                        {a.label}
                      </span>
                    </button>
                  ),
                )}
                {/* Con esqueleto, la lista que todavía no llegó ocupa su lugar latiendo:
                    las acciones de arriba ya se pueden tocar. */}
                {conEsqueleto &&
                  activa?.cargando &&
                  activa.items.length === 0 &&
                  Array.from(
                    { length: Math.max(0, TILES_DE_ESQUELETO - accionesDeLaGrilla.length) },
                    (_, i) => (
                      <TileEsqueleto
                        key={`esqueleto-${i}`}
                        claseDeTile={claseDeTile}
                        conNombre={Boolean(activa.conNombres)}
                      />
                    ),
                  )}
                {activa?.items.map((item, i) => (
                  <button
                    key={item.id}
                    type="button"
                    style={entradaEscalonada(accionesDeLaGrilla.length + i)}
                    title={item.nombre}
                    disabled={item.importando}
                    onClick={() => activa.onElegir(item)}
                    aria-pressed={item.seleccionado === undefined ? undefined : item.seleccionado}
                    data-seleccionado={item.seleccionado ? "" : undefined}
                    className={`animate-ficha-de-pestana group relative flex min-w-0 flex-col gap-1.5 text-left disabled:opacity-60 ${
                      activa.conNombres ? "" : `relative ${claseDeTile} rounded-[10px] overflow-hidden bg-neutral-100 transition-all hover:ring-2 hover:ring-brand-primary/50`
                    }`}
                  >
                    {/* Con nombre, la miniatura es un bloque propio y el nombre va debajo,
                        con el mismo aro al pasar que la biblioteca. */}
                    {activa.conNombres ? (
                      <>
                        <span
                          className={`relative block ${claseDeTile} w-full overflow-hidden rounded-xl bg-[#F0F2F7] ring-2 ring-transparent ring-offset-2 transition-shadow group-hover:ring-[rgb(var(--brand-black-rgb)_/_0.12)]`}
                        >
                          {item.url && item.esVideo ? (
                            <TileDeVideo url={item.url} />
                          ) : item.url ? (
                            // Perezosa: sólo se descarga la que está por verse. Sin
                            // esto, abrir una pestaña pedía las 60 de una.
                            <ImagenDelTile
                              key={item.url}
                              url={item.url}
                              alt=""
                              esqueleto={conEsqueleto}
                              className={`h-full w-full ${ajusteDeImagen} pointer-events-none`}
                            />
                          ) : conEsqueleto ? (
                            <span aria-hidden="true" className={`absolute inset-0 ${CLASE_ESQUELETO}`} />
                          ) : (
                            <span className="absolute inset-0 flex items-center justify-center text-[11px] text-neutral-400">
                              …
                            </span>
                          )}
                          {item.importando && (
                            <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                              <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-[12px] font-medium text-brand-black">
                          {item.nombre}
                        </span>
                        <SkuDelProducto sku={item.sku} chico />
                      </>
                    ) : item.url && item.esVideo ? (
                      <TileDeVideo url={item.url} />
                    ) : item.url ? (
                      <ImagenDelTile
                        key={item.url}
                        url={item.url}
                        alt={item.nombre}
                        esqueleto={conEsqueleto}
                        className={`w-full h-full ${ajusteDeImagen} pointer-events-none`}
                      />
                    ) : conEsqueleto ? (
                      <span aria-hidden="true" className={`absolute inset-0 ${CLASE_ESQUELETO}`} />
                    ) : (
                      <span className="absolute inset-0 flex items-center justify-center text-[11px] text-neutral-400">
                        …
                      </span>
                    )}
                    {!activa.conNombres && item.importando && (
                      <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />
                      </span>
                    )}
                    {/* Elegido en una selección múltiple: el tilde arriba a la derecha y un
                        aro azul sobre la miniatura (la del tile con nombre es su primer
                        hijo; sin nombre, el botón entero). */}
                    {item.seleccionado && (
                      <>
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none absolute inset-x-0 top-0 rounded-xl ring-2 ring-inset ring-brand-primary ${claseDeTile}`}
                        />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white shadow-sm"
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                      </>
                    )}
                  </button>
                ))}
              </div>
              {/* El centinela: también es un botón, por si algo del layout deja el
                  chequeo sin disparar. */}
              {quedaPorMostrar && onLlegarAlFinal && (
                <button
                  ref={centinelaRef}
                  type="button"
                  onClick={onLlegarAlFinal}
                  className="mt-3 flex w-full items-center justify-center gap-2 py-3 text-[12.5px] text-neutral-400 transition-colors hover:text-brand-black"
                >
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  Cargando más…
                </button>
              )}
              {/* El vacío no se lleva puestas las acciones: sin nada subido,
                  "Subir imagen" y "Generar con IA" son justamente la salida. */}
              {/* Cargando (con esqueleto) todavía no se sabe si está vacía. */}
              {activa && activa.items.length === 0 && !activa.cargando && activa.vacio && (
                <p className="text-[13px] text-neutral-400 px-1 py-8 text-center">
                  {activa.vacio}
                </p>
              )}
            </>
          )}
        </div>
        )}
        {/* El pie: fuera del scroll, pegado abajo. `pb` con el área segura del teléfono. */}
        {pie && !vista && (
          <div className="shrink-0 border-t border-black/[0.06] bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {pie}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/**
 * La tarjeta de alta del estilo `tarjeta`: la MISMA cara que "Subir nuevo producto" de los
 * pickers de los modos (`renderTileSubir` de `PickProductPanel`) y que la de Mi marca
 * —punteado celeste, degradé, el brillo de arriba y el abanico que se abre al pasar—
 * (ADR-2026-10-09-las-altas-de-elegi-que-agregar-con-la-cara-de-subir-producto).
 *
 * Mide lo mismo que la miniatura de su pestaña (`claseDeTile`, cuadrada o retrato) y el
 * texto va adentro: si llevara pie, sería más alta que las fichas y estiraría la fila.
 * La foto del frente lleva el dibujo de la acción (`dibujo`): un retrato, una pose.
 */
const TarjetaDeAlta: FC<{ accion: AccionDelSelector; className: string; style?: CSSProperties }> = ({
  accion: a,
  className,
  style,
}) => (
  <button
    type="button"
    style={style}
    onClick={a.onClick}
    disabled={a.ocupado}
    className={`${className} group relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden border-2 border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] px-2 text-center transition-[border-color,box-shadow,transform] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:pointer-events-none`}
  >
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
    />
    <span className="relative">
      <AbanicoDeFotos cargando={a.ocupado} frente={a.dibujo} />
    </span>
    <span className="relative flex flex-col items-center gap-0.5">
      <span className="text-[12.5px] sm:text-[13.5px] font-semibold leading-tight text-[#16181D]">
        {a.label}
      </span>
      {a.sublabel && !a.ocupado && (
        <span className="text-[11.5px] leading-tight text-[#6B7080]">{a.sublabel}</span>
      )}
    </span>
  </button>
);

export default SelectorDeImagenModal;
